import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import type { PolicySpec, StopSwitch } from '@reef/schema';
import { api, request, startOktaSignIn, type ConnectionSummary, type WorkflowSummary } from './api.ts';
import { ActionsView } from './views/ActionsView.tsx';
import { AnalyticsView } from './views/AnalyticsView.tsx';
import { ArtifactsView } from './views/ArtifactsView.tsx';
import { AssistantView } from './views/AssistantView.tsx';
import { ContextGraphView } from './views/ContextGraphView.tsx';
import { OperationsView } from './views/OperationsView.tsx';
import { PlatformView } from './views/PlatformView.tsx';
import { SkillsView } from './views/SkillsView.tsx';
import { FleetView } from './views/FleetView.tsx';
import { GoalsView } from './views/GoalsView.tsx';
import { KnowledgeView } from './views/KnowledgeView.tsx';
import { AdminView } from './views/AdminView.tsx';
import { AuditView } from './views/AuditView.tsx';
import { BuilderView } from './views/BuilderView.tsx';
import { BuildWithLanguageView } from './views/BuildWithLanguageView.tsx';
import { ConnectionsView } from './views/ConnectionsView.tsx';
import { FactoryAgentsView } from './views/FactoryAgentsView.tsx';
import { FactoryOverviewView } from './views/FactoryOverviewView.tsx';
import { FactoryWorkflowsView } from './views/FactoryWorkflowsView.tsx';
import { PolicyView } from './views/PolicyView.tsx';
import { ReviewQueueView } from './views/ReviewQueueView.tsx';
import { RunsView } from './views/RunsView.tsx';

type Tab =
  | 'factory'
  | 'agents'
  | 'flows'
  | 'platform'
  | 'context'
  | 'skills'
  | 'assistant'
  | 'operations'
  | 'analytics'
  | 'goals'
  | 'review'
  | 'build'
  | 'runs'
  | 'fleet'
  | 'artifacts'
  | 'connections'
  | 'approvals'
  | 'knowledge'
  | 'policies'
  | 'audit'
  | 'admin'
  | 'nl';

interface Session {
  userId: string;
  email: string;
  /** Membership of `reef-admins`, resolved live. Decides which controls render at all. */
  isAdmin: boolean;
}

/**
 * The rail, in six groups.
 *
 * The grouping is the whole point. A flat row of destinations made you read
 * every label to find the one you wanted — and they are not peers: looking at
 * the factory, looking at the platform it stands on, building a workflow,
 * watching one run, deciding what a run may do, and configuring the deployment
 * are six different reasons to be here.
 *
 * Platform sits second, directly under the factory, because that is the
 * relationship: the four destinations in it are what the seven agents above
 * them are made of, and a rail that scattered them among the configuration
 * screens would say the opposite.
 */
const NAV: ReadonlyArray<{ group: string; items: ReadonlyArray<{ tab: Tab; label: string; icon: IconName }> }> = [
  /*
   * The factory comes first, and its three destinations are the three questions
   * about it: what are the agents, what do they run, and is the whole thing
   * keeping up. Everything below is the platform those three are built on, in
   * the order it was already grouped.
   */
  {
    group: 'Software factory',
    items: [
      { tab: 'factory', label: 'Overview', icon: 'factory' },
      { tab: 'agents', label: 'Agents', icon: 'agent' },
      { tab: 'flows', label: 'Workflows', icon: 'flow' },
    ],
  },
  {
    group: 'Platform',
    items: [
      /*
       * "Summary" rather than "Overview": the factory group above already has
       * an Overview, and two rail buttons with the same label are ambiguous to
       * anybody navigating by name — a screen reader announces them
       * identically.
       */
      { tab: 'platform', label: 'Summary', icon: 'stack' },
      { tab: 'context', label: 'Context graph', icon: 'graph' },
      { tab: 'skills', label: 'Skills', icon: 'book' },
      { tab: 'assistant', label: 'Assistant', icon: 'chat' },
      /*
       * Operations sits at the bottom of the platform group rather than in
       * Configure, because it is not configuration: it is the four controls
       * somebody reaches for while the factory is doing something they did not
       * want, and they are one job rather than four screens.
       */
      { tab: 'operations', label: 'Operations', icon: 'stop' },
    ],
  },
  {
    group: 'Build',
    items: [
      { tab: 'build', label: 'Builder', icon: 'layers' },
      { tab: 'nl', label: 'Build with language', icon: 'sparkle' },
    ],
  },
  {
    group: 'Observe',
    items: [
      { tab: 'analytics', label: 'Analytics', icon: 'chart' },
      { tab: 'goals', label: 'Goals', icon: 'target' },
      { tab: 'runs', label: 'Runs', icon: 'play' },
      { tab: 'fleet', label: 'Fleet', icon: 'grid' },
      { tab: 'artifacts', label: 'Artifacts', icon: 'box' },
      { tab: 'audit', label: 'Audit', icon: 'list' },
    ],
  },
  {
    group: 'Decide',
    items: [
      /*
       * The review queue sits above Approvals rather than beside it, and the
       * two are genuinely different: Approvals is a *write* an agent staged and
       * cannot perform, this is an agent that has stopped mid-plan and is
       * waiting to be told whether to carry on. The second blocks a run; the
       * first blocks one statement.
       */
      { tab: 'review', label: 'Review Queue', icon: 'inbox' },
      { tab: 'approvals', label: 'Approvals', icon: 'check' },
      { tab: 'knowledge', label: 'Knowledge', icon: 'bulb' },
    ],
  },
  {
    group: 'Configure',
    items: [
      { tab: 'connections', label: 'Connections', icon: 'link' },
      { tab: 'policies', label: 'Policies', icon: 'shield' },
      { tab: 'admin', label: 'Admin', icon: 'sliders' },
    ],
  },
];

type IconName =
  | 'factory'
  | 'agent'
  | 'flow'
  | 'layers'
  | 'sparkle'
  | 'play'
  | 'grid'
  | 'box'
  | 'list'
  | 'check'
  | 'bulb'
  | 'link'
  | 'shield'
  | 'sliders'
  | 'stack'
  | 'graph'
  | 'book'
  | 'chat'
  | 'stop'
  | 'chart'
  | 'target'
  | 'inbox';

/** 15px, one stroke weight, `currentColor` — so the rail tints them, not the icon set. */
const ICONS: Record<IconName, string> = {
  // Three that read as the studio's own: a plant, an agent, a branching flow.
  factory: 'M2.4 13.6h11.2M3.2 13.6V7.2l3.4 2.2V7.2l3.4 2.2V4.2h3.2v9.4',
  agent: 'M8 2.6a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM3.6 9.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM12.4 9.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM8 6v2.2M8 8.2 4.6 9.9M8 8.2l3.4 1.7',
  flow: 'M8 1.9v3.4M8 10.7v3.4M4.2 8h7.6M8 5.3a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7ZM3 6.65a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7ZM13 6.65a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7Z',
  layers: 'M8 1.6 14.4 5 8 8.4 1.6 5 8 1.6ZM2.4 8 8 11 13.6 8M2.4 11 8 14 13.6 11',
  sparkle: 'M8 2v3M8 11v3M2.5 8h3M10.5 8h3M4.4 4.4l2 2M9.6 9.6l2 2M11.6 4.4l-2 2M6.4 9.6l-2 2',
  play: 'M8 14.4A6.4 6.4 0 1 0 8 1.6a6.4 6.4 0 0 0 0 12.8ZM6.6 5.6l4 2.4-4 2.4V5.6Z',
  grid: 'M2.4 2.4h4.4v4.4H2.4V2.4ZM9.2 2.4h4.4v4.4H9.2V2.4ZM2.4 9.2h4.4v4.4H2.4V9.2ZM9.2 9.2h4.4v4.4H9.2V9.2Z',
  box: 'M2 5.2 8 2l6 3.2v5.6L8 14l-6-3.2V5.2ZM2 5.2 8 8.4l6-3.2M8 8.4V14',
  list: 'M5.4 4h8.2M5.4 8h8.2M5.4 12h8.2M2.6 4h.01M2.6 8h.01M2.6 12h.01',
  check: 'M14.4 8A6.4 6.4 0 1 1 8 1.6M5.6 7.8 7.8 10l6-6.2',
  bulb: 'M6.2 12.4h3.6M6.6 14.2h2.8M8 1.8a4 4 0 0 1 2.4 7.2c-.4.3-.6.7-.6 1.1H6.2c0-.4-.2-.8-.6-1.1A4 4 0 0 1 8 1.8Z',
  link: 'M6.6 9.4a2.6 2.6 0 0 0 3.9.3l2-2a2.6 2.6 0 0 0-3.7-3.7l-1.1 1.1M9.4 6.6a2.6 2.6 0 0 0-3.9-.3l-2 2a2.6 2.6 0 0 0 3.7 3.7l1.1-1.1',
  shield: 'M8 1.8 13.2 4v4c0 3-2.2 5.2-5.2 6.2C5 13.2 2.8 11 2.8 8V4L8 1.8Z',
  sliders: 'M3 4.6h10M3 8h10M3 11.4h10M6.2 4.6a1.2 1.2 0 1 0 2.4 0 1.2 1.2 0 0 0-2.4 0ZM9.4 11.4a1.2 1.2 0 1 0 2.4 0 1.2 1.2 0 0 0-2.4 0Z',
  // The platform four: a stack of pieces, a graph, a book of skills, a question.
  stack: 'M2.4 11.6h11.2M2.4 8h11.2M2.4 4.4h11.2',
  graph: 'M4 11.4a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2ZM12 6.6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2ZM12 13.4a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2ZM5.4 8.8 10.6 5.6M5.4 10.2l5.2 1.6',
  book: 'M3 3.2h4.2c.9 0 1.6.7 1.6 1.6v8c0-.7-.6-1.3-1.4-1.3H3V3.2ZM13 3.2H8.8c-.9 0-1.6.7-1.6 1.6v8c0-.7.6-1.3 1.4-1.3H13V3.2Z',
  chat: 'M13.4 9.4a1.6 1.6 0 0 1-1.6 1.6H5.4L2.6 13.4V4.2a1.6 1.6 0 0 1 1.6-1.6h7.6a1.6 1.6 0 0 1 1.6 1.6v5.2Z',
  stop: 'M8 14.4A6.4 6.4 0 1 0 8 1.6a6.4 6.4 0 0 0 0 12.8ZM6 6h4v4H6V6Z',
  // Analytics, goals, and the queue: a rising series, a target, a tray.
  chart: 'M2.4 13.2h11.2M4.2 11.4V7.2M7.4 11.4V4.2M10.6 11.4V8.8M13 11.4V5.6',
  target: 'M8 14.2A6.2 6.2 0 1 0 8 1.8a6.2 6.2 0 0 0 0 12.4ZM8 11.2A3.2 3.2 0 1 0 8 4.8a3.2 3.2 0 0 0 0 6.4ZM8 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  inbox: 'M2.4 9.4h3l1 1.8h3.2l1-1.8h3M2.4 9.4 4.2 3.4h7.6l1.8 6v3.2a1 1 0 0 1-1 1H3.4a1 1 0 0 1-1-1V9.4Z',
};

function RailIcon({ name }: { name: IconName }) {
  return (
    <svg className="rail-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d={ICONS[name]}
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The factory's workflows, in the order work moves through them.
 *
 * Duplicated from the Worker rather than fetched, and deliberately: it is used
 * to *order* a list the control plane already returned, so a network round trip
 * to learn an ordering would leave the list flickering into place. The names
 * are checked against what the server sends — anything not deployed simply does
 * not appear.
 */
const FACTORY_WORKFLOW_IDS = [
  'factory-spec-to-prototype',
  'factory-triage',
  'factory-codegen',
  'factory-inner-loop',
  'factory-validation',
  'factory-ci',
  'factory-release',
  'factory-documentation',
  'factory-monitoring',
  'factory-maintenance',
  'factory-incident-mining',
  'factory-sdlc',
];

export function App() {
  /*
   * Still the builder. The factory sits at the top of the rail because it is
   * what most people come here to look at, but the screen you land on is a
   * separate decision from the order of the navigation — and moving it is a
   * change to everybody's habit, not a consequence of adding three screens.
   */
  const [tab, setTab] = useState<Tab>('build');
  const [session, setSession] = useState<Session | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [connections, setConnections] = useState<ConnectionSummary[]>([]);
  const [policies, setPolicies] = useState<PolicySpec[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  const [pendingActions, setPendingActions] = useState(0);
  /** How many steps have stopped and are waiting on somebody. The rail's badge. */
  const [pendingReviews, setPendingReviews] = useState(0);
  /** The deployment-wide stop switch, when one is engaged. Null is the normal state. */
  const [stopped, setStopped] = useState<StopSwitch | null>(null);
  const [pendingKnowledge, setPendingKnowledge] = useState(0);
  const [fatal, setFatal] = useState<string | null>(null);

  const refreshWorkflows = useCallback(() => {
    api
      .listWorkflows()
      .then((result) => setWorkflows(result.workflows))
      .catch((error) => setFatal((error as Error).message));
  }, []);

  /*
   * Counted on the tab for the same reason Approvals is: a proposal nobody
   * looks at is a proposal that expires into irrelevance, and the only place
   * somebody reliably sees it is the navigation they were already using.
   */
  const refreshKnowledge = useCallback(() => {
    request<{ proposals: unknown[] }>('/knowledge?state=proposed')
      .then((body) => setPendingKnowledge(body.proposals.length))
      .catch(() => {
        // A count that cannot be fetched is not worth a banner over the whole
        // shell; the Knowledge screen reports the failure when opened.
      });
  }, []);

  const refreshReviewQueue = useCallback(() => {
    api
      .reviewQueue()
      .then((result) => setPendingReviews(result.count))
      .catch(() => {
        // A queue that cannot be counted is not worth an error banner over the
        // whole shell; the screen itself reports the failure when opened.
      });
  }, []);

  /*
   * Sequenced, because these responses can land out of order. The list is
   * re-fetched after every write, and a slower fetch issued *before* that write
   * would otherwise install pre-write data over the top of it. That reads to
   * the person at the screen as a save that silently came undone a moment after
   * it landed, which is worse than a save that visibly failed.
   */
  const connectionsRequest = useRef(0);
  const refreshConnections = useCallback(() => {
    const request = ++connectionsRequest.current;
    api
      .listConnections()
      .then((result) => {
        if (request === connectionsRequest.current) setConnections(result.connections);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/auth/get-session', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then(async (body: { user?: { id: string; email: string } } | null) => {
        if (!body?.user) return startOktaSignIn('/');
        // better-auth's session says nothing about the reef-admins group, and
        // the UI has to know before it renders controls the API would refuse.
        const me = await api.me().catch(() => null);
        setSession({
          userId: body.user.id,
          email: body.user.email,
          isAdmin: me?.isAdmin ?? false,
        });
      })
      .catch((error) => setFatal((error as Error).message || 'Could not reach the reef API.'));
  }, []);

  useEffect(() => {
    if (!session) return;
    refreshWorkflows();
    refreshConnections();
    api.listPolicies().then((result) => setPolicies(result.policies)).catch(() => {});
    api.listSkills().then((result) => setSkills(result.skills)).catch(() => {});
    api
      .listActions({ state: 'staged' })
      .then((result) => setPendingActions(result.actions.length))
      .catch(() => {});
    refreshKnowledge();

    /*
     * Whether the factory is stopped, on every screen.
     *
     * In the topbar rather than only on Operations, because the fact somebody
     * needs is "nothing is running and here is why" — and they will be looking
     * at Runs or the builder when they need it, wondering why their trigger
     * did nothing. A control whose state is only visible on the screen that
     * owns it is one people rediscover by asking in a channel.
     */
    /*
     * The review count is fetched here rather than by the queue itself, because
     * the badge has to be right on every screen — somebody looking at Runs is
     * exactly the person who needs to know a step has stopped and is waiting
     * for them.
     */
    refreshReviewQueue();

    api
      .stopSwitches()
      .then((result) => setStopped(result.live.find((entry) => entry.scope === null) ?? null))
      .catch(() => {});
  }, [session, refreshWorkflows, refreshConnections, refreshKnowledge, refreshReviewQueue]);

  // A dual-identity connection with a working bot is still "authorized" for
  // unattended runs, so counting `!authorized` would hide the link *you* owe.
  const needsAttention = connections.filter(
    (connection) => (connection.usesUser && !connection.userLinked) || !connection.authorized,
  );

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">
          <span className="brand-mark" aria-hidden="true" />
          reef
        </span>

        <span className="spacer" />

        {stopped && (
          <button
            className="pill warn"
            title={`${stopped.reason} — engaged by ${stopped.engagedBy ?? 'an operator'}`}
            onClick={() => setTab('operations')}
          >
            the factory is stopped
          </button>
        )}
        {needsAttention.length > 0 && (
          <button
            className="pill warn"
            title={needsAttention.map((entry) => entry.name).join(', ')}
            onClick={() => setTab('connections')}
          >
            {needsAttention.length} connection{needsAttention.length === 1 ? '' : 's'} to authorize
          </button>
        )}
        {session && <span className="hint">{session.email}</span>}
      </header>

      <div className="shell">
        <nav className="rail" aria-label="Sections">
          {NAV.map((section) => (
            <Fragment key={section.group}>
              <p className="rail-group">{section.group}</p>
              {section.items.map((item) => {
                const count =
                  item.tab === 'approvals'
                    ? pendingActions
                    : item.tab === 'review'
                      ? pendingReviews
                      : item.tab === 'knowledge'
                        ? pendingKnowledge
                        : 0;
                return (
                  <button
                    key={item.tab}
                    className="rail-item"
                    aria-current={tab === item.tab}
                    /*
                     * The count is a chip on screen and a parenthesis to a
                     * screen reader, so "Approvals (2)" is still the name of
                     * the control however you are reading it.
                     */
                    aria-label={count > 0 ? `${item.label} (${count})` : item.label}
                    onClick={() => setTab(item.tab)}
                  >
                    <RailIcon name={item.icon} />
                    {item.label}
                    {count > 0 && <span className="rail-count">{count}</span>}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </nav>

        <div className="workspace">
          {fatal ? (
        <div className="main">
          <div className="banner danger">
            <h3>reef is unreachable</h3>
            <p style={{ margin: 0 }}>{fatal}</p>
          </div>
        </div>
      ) : !session ? (
        <div className="main">
          <div className="empty">Signing you in with Okta…</div>
        </div>
      ) : tab === 'factory' ? (
        <FactoryOverviewView />
      ) : tab === 'agents' ? (
        <FactoryAgentsView />
      ) : tab === 'flows' ? (
        <FactoryWorkflowsView workflows={workflows} factoryIds={FACTORY_WORKFLOW_IDS} />
      ) : tab === 'platform' ? (
        <PlatformView />
      ) : tab === 'context' ? (
        <ContextGraphView />
      ) : tab === 'skills' ? (
        <SkillsView />
      ) : tab === 'assistant' ? (
        <AssistantView />
      ) : tab === 'operations' ? (
        <OperationsView isAdmin={session.isAdmin} />
      ) : tab === 'analytics' ? (
        <AnalyticsView />
      ) : tab === 'goals' ? (
        <GoalsView />
      ) : tab === 'review' ? (
        <ReviewQueueView onDecided={refreshReviewQueue} />
      ) : tab === 'build' ? (
        <BuilderView
          workflows={workflows}
          connections={connections}
          policies={policies}
          skills={skills}
          onWorkflowsChanged={refreshWorkflows}
        />
      ) : tab === 'runs' ? (
        <RunsView workflows={workflows} />
      ) : tab === 'fleet' ? (
        <FleetView isAdmin={session.isAdmin} />
      ) : tab === 'artifacts' ? (
        <ArtifactsView workflows={workflows} />
      ) : tab === 'knowledge' ? (
        <KnowledgeView onChanged={refreshKnowledge} />
      ) : tab === 'connections' ? (
        <ConnectionsView
          connections={connections}
          isAdmin={session.isAdmin}
          onRefresh={refreshConnections}
        />
      ) : tab === 'approvals' ? (
        <ActionsView />
      ) : tab === 'admin' ? (
        <AdminView isAdmin={session.isAdmin} />
      ) : tab === 'policies' ? (
        <PolicyView
          policies={policies}
          onChanged={() =>
            api.listPolicies().then((result) => setPolicies(result.policies)).catch(() => {})
          }
        />
      ) : tab === 'audit' ? (
        <AuditView workflows={workflows} />
      ) : (
        <BuildWithLanguageView userId={session.userId} onWorkflowsChanged={refreshWorkflows} />
      )}
        </div>
      </div>
    </div>
  );
}
